import { FormioComponenTemplateContext, FormioComponent } from '../types';

type FormioComponenTemplateContextPlus = FormioComponenTemplateContext & {
  sidebar: string;
  form: string;
  pages: [];
  self: { page: number };
  t: (trans: string, params?: { [key: string]: boolean }) => string;
  iconClass: (arg: string) => string;
  componentInfo: { title: string };
  editForm: string;
  preview: string;
  component: { legend: string; tooltip: string; collapsible: boolean };
  collapsed: boolean;
  nestedKey: string;
  type: string;
  instance: { schemas: Record<string, FormioComponent> };
  key: string;
  html: string;
  children: string;
};

const findNestedComponentRecursive = (
  components: FormioComponent[],
  componentKey: string,
): FormioComponent | undefined => {
  if (!components) {
    return undefined;
  }

  return components.reduce(
    (result: FormioComponent, component: FormioComponent) => {
      if (component.key === componentKey) {
        return component;
      }

      const foundComponent = findNestedComponentRecursive(
        component.components || component.columns || [],
        componentKey,
      );

      return foundComponent ?? result;
    },
    {} as FormioComponent,
  );
};

const findNestedComponentInSchemas = (
  schemas: Record<string, FormioComponent>,
  componentKey: string,
) => {
  if (schemas[componentKey]) {
    return schemas[componentKey];
  }

  const found = Object.keys(schemas).reduce(
    (result: FormioComponent | undefined, key: string) => {
      const foundComponent = findNestedComponentRecursive(
        schemas[key].components as FormioComponent[],
        componentKey,
      );

      return foundComponent ?? result;
    },
    undefined,
  );

  return found;
};

const builder = {
  form: (ctx: { sidebar: string; form: string }) => `
    <div class="formio builder row formbuilder">
      <div class="col-xs-4 col-sm-3 col-md-3 formcomponents">
        ${ctx.sidebar}
      </div>
      <div class="col-xs-8 col-sm-9 col-md-9 formarea" ref="form">
        ${ctx.form}
      </div>
    </div>
  `,
};

const builderWizard = {
  form: (ctx: FormioComponenTemplateContextPlus) => `
    <div class="formio builder row formbuilder">
      <div class="col-xs-4 col-sm-3 col-md-3 formcomponents">
        ${ctx.sidebar}
      </div>
      <div class="col-xs-8 col-sm-9 col-md-9 formarea">
        <ol class="breadcrumb">
          ${ctx.pages.reduce(
            (acc: {}, page: { title: string }, pageIndex: number) =>
              acc +
              `
              <li>
                <span
                  title="${page.title}"
                  class="mr-2 badge ${
                    pageIndex === ctx.self.page ? 'badge-primary' : 'badge-info'
                  } wizard-page-label"
                  ref="gotoPage"
                >
                  ${page.title}
                </span>
              </li>
            `,
            '',
          )}
          <li>
            <span
              title="${ctx.t('Create Page')}"
              class="mr-2 badge badge-success wizard-page-label"
              ref="addPage"
            >
              <i class="${ctx.iconClass('plus')}"></i> ${ctx.t('Page')}
            </span>
          </li>
        </ol>
        <div ref="form">
          ${ctx.form}
        </div>
      </div>
    </div>
  `,
};

const builderComponent = {
  form: (ctx: FormioComponenTemplateContext) => {
    const showRemoveBtn = ctx.childComponent.builder
      ? !ctx.childComponent.builder.disableRemove
      : true;

    const editableByEmpOnly = ctx.childComponent?.properties?.editableByEmpOnly;

    const showEditBtn = ctx.childComponent.builder
      ? !ctx.childComponent.builder.disableEdit && !editableByEmpOnly
      : true;

    const noDragClass = ctx.childComponent.builder?.disableDrag
      ? 'no-drag'
      : '';

    return `<div class="${noDragClass} builder-component builder-component-${
      ctx.childComponent.type
    }" ref="dragComponent">
      ${
        !ctx.disableBuilderActions
          ? `
        <div class="component-btn-group" data-noattach="true">
          ${
            showRemoveBtn
              ? `<div
            role="button"
            aria-label="Remove button. Click to remove component from the form"
            tabindex="-1"
            class="btn btn-xxs btn-danger component-settings-button component-settings-button-remove"
            ref="removeComponent"
          >
            <i class="${ctx.iconClass('remove')}"></i>
          </div>`
              : ''
          } ${
              showEditBtn
                ? `<div
              role="button"
              aria-label="Edit button. Click to open component settings modal window"
              tabindex="-1"
              class="btn btn-xxs btn-secondary component-settings-button component-settings-button-edit"
              ref="editComponent"
            >
              <i class="${ctx.iconClass('cog')}"></i>
            </div>
          `
                : ''
            } </div>`
          : ''
      }
      ${ctx.html}
    </div>`;
  },
};

const builderEditForm = {
  form: (ctx: FormioComponenTemplateContextPlus) => `
    <div class="row">
      <div class="col col-sm-6">
        <p class="lead">${ctx.t(ctx.componentInfo.title)} ${ctx.t(
    'Component',
  )}</p>
      </div>
    </div>
    <div class="row">
      <div class="col col-sm-6">
        <div ref="editForm">
            ${ctx.editForm}
        </div>
      </div>
      <div class="col col-sm-6">
        <div class="card panel preview-panel">
          <div class="card-header">
            <h4 class="card-title mb-0">${ctx.t('Preview')}</h4>
          </div>
          <div class="card-body">
            <div class="component-preview" ref="preview">
              ${ctx.preview}
            </div>
          </div>
        </div>
        <div style="margin-top: 10px;">
          <button class="btn btn-success" style="margin-right: 10px;" ref="saveButton">${ctx.t(
            'Save',
          )}</button>
          <button class="btn btn-secondary" style="margin-right: 10px;" ref="cancelButton">${ctx.t(
            'Cancel',
          )}</button>
        </div>
      </div>
    </div>
  `,
};

const fieldset = {
  form: (ctx: FormioComponenTemplateContextPlus) => `
    <fieldset>
      ${
        ctx.component.legend
          ? `
            <legend ref="header" class="${
              ctx.component.collapsible ? 'formio-clickable' : ''
            }">
            ${ctx.t(ctx.component.legend, { _userInput: true })}
            ${
              ctx.component.tooltip
                ? `<i ref="tooltip" tabindex="0" class="${ctx.iconClass(
                    'question-sign',
                  )} text-muted" data-tooltip="${ctx.component.tooltip}"></i>`
                : ''
            }
            </legend><hr/>`
          : ''
      }
      ${
        !ctx.collapsed
          ? `
          <div class="fieldset-body" ref="${ctx.nestedKey}">
            ${ctx.children}
          </div>`
          : ''
      }
    </fieldset>`,
};

const builderComponents = {
  form: (ctx: FormioComponenTemplateContextPlus) => {
    const found = findNestedComponentInSchemas(ctx.instance.schemas, ctx.key);
    const noDrag = found?.builder?.disableDrag ? 'no-drag' : '';
    const noDrop = found?.builder?.disableDrop ? 'no-drop' : '';

    let _t,
      _p = '';
    _p +=
      `<div class="${noDrag} ${noDrop} builder-components drag-container formio-builder-` +
      ((_t = ctx.type) == null ? '' : _t) +
      '" ref="' +
      ((_t = ctx.key) == null ? '' : _t) +
      '-container">\n  ' +
      ((_t = ctx.html) == null ? '' : _t) +
      '\n</div>\n';

    return _p;
  },
};

export {
  builder,
  builderWizard,
  builderEditForm,
  builderComponent,
  builderComponents,
  fieldset,
};
